/**
 * Pricing Table V2 Component
 *
 * Redesigned pricing table with Kit-inspired layout:
 * - Slider on left, toggle on right
 * - 3 confirmation benefits header
 * - Categorized benefits with tooltips
 * - Intro pricing display
 * - Plans disable when slider exceeds their threshold
 *
 * @module components/billing/PricingTableV2
 */

import { cn } from '@/lib/utils';
import { Repeat, Zap, DollarSign } from 'lucide-react';
import {
  type PlanId,
  type BillingInterval,
  SLIDER_STOPS,
  getOrderedPlanTiers,
  isPlanDisabledAtRequestCount,
  getBucketForRequestCount,
  formatRequestCount,
} from '@hummingbird/pricing';
import { PlanCardV2 } from './PlanCardV2';

// =============================================================================
// Types
// =============================================================================

export interface PricingTableV2Props {
  /** Current user's plan (if logged in) */
  currentPlanId?: PlanId;
  /** Current user's bucket max (for downgrade detection) */
  currentBucketMax?: number;
  /** Whether user is eligible for intro pricing */
  isEligibleForIntro?: boolean;
  /** Callback when user selects a plan */
  onSelectPlan?: (planId: PlanId, requests: number, interval: BillingInterval) => void;
  /** Whether an action is in progress */
  isLoading?: boolean;
  /** Loading plan ID (if specific plan is loading) */
  loadingPlanId?: PlanId;
  /** Controlled billing interval */
  interval: BillingInterval;
  /** Callback when interval changes */
  onIntervalChange: (interval: BillingInterval) => void;
  /** Controlled slider index */
  sliderIndex: number;
  /** Callback when slider index changes */
  onSliderIndexChange: (index: number) => void;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Confirmation Benefits
// =============================================================================

const CONFIRMATION_BENEFITS = [
  {
    icon: Repeat,
    text: 'Cancel anytime (no lock-in)',
  },
  {
    icon: Zap,
    text: 'Same API, higher limits',
  },
  {
    icon: DollarSign,
    text: 'Pro-rated upgrades, no surprises',
  },
];

// =============================================================================
// Component
// =============================================================================

export function PricingTableV2({
  currentPlanId,
  currentBucketMax = 0,
  isEligibleForIntro = true,
  onSelectPlan,
  isLoading = false,
  loadingPlanId,
  interval,
  onIntervalChange,
  sliderIndex,
  onSliderIndexChange,
  className,
}: PricingTableV2Props) {
  const currentStop = SLIDER_STOPS[sliderIndex];
  const selectedRequests = currentStop?.requests ?? 10000;

  const orderedPlans = getOrderedPlanTiers();

  // Determine if selecting a plan at the current slider position would be a downgrade
  const isDowngradeForPlan = (planId: PlanId): boolean => {
    if (!currentPlanId || currentPlanId === 'free' || currentBucketMax === 0) {
      return false;
    }
    const bucket = getBucketForRequestCount(selectedRequests, planId);
    if (!bucket) return false;
    // It's a downgrade if the selected bucket is meaningfully smaller than current bucket
    // Use tolerance since bucket maxRequests (19999) vs metadata (20000) can differ slightly
    return bucket.maxRequests < (currentBucketMax - 1000);
  };

  const handlePlanSelect = (planId: PlanId) => {
    const bucket = getBucketForRequestCount(selectedRequests, planId);
    onSelectPlan?.(planId, bucket?.maxRequests ?? selectedRequests, interval);
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Confirmation Benefits Header */}
      <div className="flex flex-wrap justify-center gap-6 mb-8 text-sm text-gray-600">
        {CONFIRMATION_BENEFITS.map((benefit, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <benefit.icon className="w-4 h-4 text-brand-500" />
            <span>{benefit.text}</span>
          </div>
        ))}
      </div>

      {/* Controls Row: Slider on left, Toggle on right */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10 p-6 bg-gray-50 rounded-xl">
        {/* Slider Section */}
        <div className="flex-1 max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            How many requests do you need?
          </label>
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-gray-900 min-w-[80px]">
              {formatRequestCount(selectedRequests)}
            </span>
            <input
              type="range"
              min={0}
              max={SLIDER_STOPS.length - 1}
              value={sliderIndex}
              onChange={(e) => onSliderIndexChange(parseInt(e.target.value, 10))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(sliderIndex / (SLIDER_STOPS.length - 1)) * 100}%, #e5e7eb ${(sliderIndex / (SLIDER_STOPS.length - 1)) * 100}%, #e5e7eb 100%)`,
              }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-400">
            <span>1K</span>
            <span>5M+</span>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center gap-2 p-1 bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => onIntervalChange('monthly')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors',
              interval === 'monthly'
                ? 'bg-brand-500 text-white'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Pay monthly
          </button>
          <button
            onClick={() => onIntervalChange('annual')}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors relative',
              interval === 'annual'
                ? 'bg-brand-500 text-white'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            Pay yearly
            <span className={cn(
              'ml-2 text-xs px-1.5 py-0.5 rounded',
              interval === 'annual'
                ? 'bg-white/20 text-white'
                : 'bg-success-100 text-success-700'
            )}>
              2 months free
            </span>
          </button>
        </div>
      </div>

      {/* Plan Cards Grid - 5 columns on xl */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {orderedPlans.map((plan) => {
          const isDisabled = isPlanDisabledAtRequestCount(plan.id, selectedRequests);
          const bucket = getBucketForRequestCount(selectedRequests, plan.id);
          const isDowngrade = isDowngradeForPlan(plan.id);

          // "Current Plan" only if same plan AND same bucket
          // Use tolerance for bucket comparison since metadata stores round numbers (20000)
          // but actual bucket maxRequests might be 19999
          const isSamePlan = currentPlanId === plan.id;
          const isFreeWithNoBucket = plan.id === 'free' && currentBucketMax === 0;
          const bucketMatchesTolerance = bucket && currentBucketMax > 0 &&
            Math.abs(bucket.maxRequests - currentBucketMax) <= 1000;
          // For paid plans, only mark as current if bucket actually matches
          // For free plan, mark as current if user is on free (no bucket)
          const isCurrentPlanAndBucket = isSamePlan && (bucketMatchesTolerance || isFreeWithNoBucket);

          return (
            <PlanCardV2
              key={plan.id}
              plan={plan}
              interval={interval}
              bucket={bucket}
              selectedRequests={selectedRequests}
              isCurrentPlan={isCurrentPlanAndBucket}
              isHighlighted={plan.id === 'pro'}
              isDisabled={isDisabled}
              isEligibleForIntro={isEligibleForIntro}
              isDowngrade={isDowngrade}
              onSelect={() => handlePlanSelect(plan.id)}
              isLoading={isLoading && loadingPlanId === plan.id}
            />
          );
        })}
      </div>

      {/* Enterprise CTA */}
      <div className="mt-10 text-center">
        <p className="text-gray-500">
          Need more than 5M reqs/mo or custom features?{' '}
          <a
            href="mailto:sales@hummingbirdapi.com"
            className="text-brand-600 hover:text-brand-700 font-medium"
          >
            Contact our sales team
          </a>
        </p>
      </div>
    </div>
  );
}
